import Phaser from 'phaser';
import { apiClient } from '../network/ApiClient';
import { GameSoundManager } from '../audio/GameSoundManager';
import { SessionChannel } from '../network/SessionChannel';
import { recordGameExit } from '../debug/exitBreadcrumbs';
import { IS_MOBILE_EXPERIENCE } from '../utils/deviceMode';
import type { GameState } from '../types/game';

// ── Theme tokens (inline — avoids importing two theme files into a Phaser scene) ──
const T = {
  // Backgrounds
  pageBg:    0x0a0a0a,
  cardBg:    0x1a1a1a,
  barTrack:  0x2a2a2a,

  // Gold
  gold:      0xD4920A,
  goldStr:   '#D4920A',

  // Text
  white:     '#ffffff',
  muted:     '#6B7280',
  secondary: '#9CA3AF',
  danger:    '#dc2626',

  // Team
  teamBlue:  0x4A90D9,
  teamRed:   0xE05252,

  // Radius
  cardR:   16,
  btnR:    12,
} as const;

// Device pixel ratio for sharp text — clamped to 3 to avoid memory issues
const DPR = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 2, 3) : 2;

interface GameOverData {
  gameState:         GameState;
  sessionId:         number;
  teamScores:        Record<string, number>;
  myPosition:        number;
  myTeam:            number;
  sessionOver?:      boolean;
  sessionWinnerTeam?: number | null;
  targetScore?:      number;
  useDesktopRender?: boolean;
}

// NOTE: GameOverScene is NOT in the production Phaser scene list on feature/round-end-react-overlay.
// React owns the round-end overlay (phaser:round-end event). This scene is kept alive only
// for DevGameOverScreen (the /dev/game-over preview route). Do not re-add to GameScreen's scene
// array without removing the React overlay first.
export class GameOverScene extends Phaser.Scene {
  private gameState!:         GameState;
  private sessionId!:         number;
  private teamScores:         Record<string, number> = {};
  private myPosition!:        number;
  private myTeam!:            number;
  private sessionOver         = false;
  private sessionWinnerTeam:  number | null = null;
  private targetScore         = 100;
  private useDesktopRender    = false;

  private sessionChannel:   SessionChannel | null          = null;
  private autoAdvanceTimer: Phaser.Time.TimerEvent | null = null;
  private fallbackTimer:    Phaser.Time.TimerEvent | null = null;
  private countdownText:    Phaser.GameObjects.Text | null = null;

  // State machine replacing the old _navigated boolean.
  //   idle          — waiting for auto-advance or user tap
  //   creating_next — REST nextGame in flight
  //   transitioning — scene.start('GameScene') called; no further action allowed
  //
  // INVARIANT: transitionState === 'transitioning' iff transitionTargetGameId is set.
  // Reset in init() (not shutdown()) so each new round starts clean.
  private transitionState: 'idle' | 'creating_next' | 'transitioning' = 'idle';
  private transitionTargetGameId?: number;

  // Scene-level tap router for the "Next Round" / "Back Home" buttons.
  // Stores world-space bounds so the single pointerdown listener can hit-test them.
  private _btnRouter: ((ptr: Phaser.Input.Pointer) => void) | null = null;
  private _btnBounds: Array<{ rect: Phaser.Geom.Rectangle; cb: () => void }> = [];

  constructor() {
    super({ key: 'GameOverScene' });
  }

  init(data: GameOverData): void {
    this.gameState         = data.gameState;
    this.sessionId         = data.sessionId;
    this.teamScores        = data.teamScores;
    this.myPosition        = data.myPosition;
    this.myTeam            = data.myTeam;
    this.sessionOver       = data.sessionOver ?? false;
    this.sessionWinnerTeam = data.sessionWinnerTeam ?? null;
    this.targetScore       = data.targetScore ?? 100;
    this.useDesktopRender  = data.useDesktopRender ?? false;

    // Reset state machine for every new round.
    // Phaser reuses the scene instance across rounds (shutdown → init → create),
    // so we MUST reset here — not in shutdown(), where we deliberately leave the
    // state intact so stale async callbacks can detect the scene is gone.
    this.setTransitionState('idle', 'init');
    this.transitionTargetGameId = undefined;
    this.autoAdvanceTimer       = null;
    this.fallbackTimer          = null;
    this.sessionChannel         = null;
    this.countdownText          = null;
    this._btnRouter             = null;
    this._btnBounds             = [];
  }

  async create(): Promise<void> {
    this.cameras.main.fadeIn(300);

    const w = this.scale.width;
    const h = this.scale.height;

    // Full-screen background
    const bgGfx = this.add.graphics();
    bgGfx.fillStyle(T.pageBg, 1);
    bgGfx.fillRect(0, 0, w, h);

    const t0 = this.teamScores['0'] ?? this.teamScores['team1'] ?? 0;
    const t1 = this.teamScores['1'] ?? this.teamScores['team2'] ?? 0;

    const sessionFinished =
      this.sessionOver || t0 >= this.targetScore || t1 >= this.targetScore;

    const winnerTeam =
      this.sessionWinnerTeam !== null
        ? this.sessionWinnerTeam
        : t0 >= this.targetScore ? 0 : 1;

    const myTeamWon = winnerTeam === this.myTeam;

    if (sessionFinished) {
      GameSoundManager.playSessionEnd(myTeamWon);
      this.drawSessionOver(w, h, t0, t1, winnerTeam);
    } else {
      GameSoundManager.playGameResult(this.gameState.winner_team === this.myTeam);
      this.drawRoundOver(w, h, t0, t1);

      // Subscribe to session channel for next-game event
      this.sessionChannel = new SessionChannel(this.sessionId);
      this.sessionChannel.onNextGameCreated((msg) => {
        console.log(
          `[DIAG:GameOver] WS next_game_created game=${msg.game_id}` +
          ` session=${this.sessionId} state=${this.transitionState}` +
          ` targetId=${this.transitionTargetGameId ?? 'none'}` +
          ` active=${this.scene.isActive()} timerAlive=${this.autoAdvanceTimer !== null}`,
        );
        this.transitionToNextGame(msg.game_id, 'WS', msg.team_scores);
      });
      try {
        await this.sessionChannel.subscribe();
      } catch (err) {
        console.error('[GameOverScene] Failed to subscribe to session channel:', err);
      }
    }
  }

  // ── Shared layout helpers ─────────────────────────────────────────────────

  /** Safe content width: max 420px on mobile, 480px on desktop, with 22px margins. */
  private contentWidth(w: number): number {
    return IS_MOBILE_EXPERIENCE
      ? Math.min(w - 44, 420)
      : Math.min(w - 80, 480);
  }

  /** Render a text object at integer position with DPR resolution (no blur). */
  private txt(
    x: number,
    y: number,
    content: string,
    style: Phaser.Types.GameObjects.Text.TextStyle,
    originX = 0.5,
    originY = 0,
  ): Phaser.GameObjects.Text {
    return this.add.text(Math.round(x), Math.round(y), content, {
      resolution: DPR,
      ...style,
    }).setOrigin(originX, originY);
  }

  /** Small gold overline label, e.g. "ROUND 1" or "SESSION OVER". */
  private overline(cx: number, y: number, text: string): number {
    this.txt(cx, y, text, {
      fontSize:      '11px',
      color:         T.goldStr,
      fontFamily:    'Montserrat, sans-serif',
      fontStyle:     'bold',
      letterSpacing: 2.5,
    });
    return y + 20;
  }

  /** Thin gold divider line centered on cx. */
  private divider(cx: number, y: number, contentW: number): void {
    const gfx = this.add.graphics();
    gfx.lineStyle(1, T.gold, 0.20);
    gfx.beginPath();
    gfx.moveTo(Math.round(cx - contentW * 0.4), Math.round(y));
    gfx.lineTo(Math.round(cx + contentW * 0.4), Math.round(y));
    gfx.strokePath();
  }

  /**
   * Themed gold primary button — full content width, 52px tall, radius 12px.
   * Returns the y of the button's bottom edge.
   */
  private goldButton(
    cx: number,
    topY: number,
    contentW: number,
    label: string,
    onClick: () => void,
  ): number {
    const btnH = 52;
    const cy   = Math.round(topY + btnH / 2);
    const bw   = Math.round(contentW);

    const bg = this.add.graphics();
    bg.fillStyle(T.gold, 1);
    bg.fillRoundedRect(Math.round(cx - bw / 2), Math.round(cy - btnH / 2), bw, btnH, T.btnR);

    const hoverBg = this.add.graphics();
    hoverBg.fillStyle(0xE8A514, 1);   // slightly lighter gold on hover
    hoverBg.fillRoundedRect(Math.round(cx - bw / 2), Math.round(cy - btnH / 2), bw, btnH, T.btnR);
    hoverBg.setAlpha(0);

    const lbl = this.txt(cx, cy, label, {
      fontSize:   '16px',
      color:      '#000000',
      fontFamily: 'Nunito, sans-serif',
      fontStyle:  'bold',
    }, 0.5, 0.5);

    const hitArea = new Phaser.Geom.Rectangle(
      Math.round(cx - bw / 2), Math.round(cy - btnH / 2), bw, btnH,
    );
    const container = this.add.container(0, 0, [bg, hoverBg, lbl]);
    container.setSize(bw, btnH);
    container.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    container.on('pointerover', () => hoverBg.setAlpha(1));
    container.on('pointerout',  () => hoverBg.setAlpha(0));

    // Register this button's bounds with the scene-level tap router instead of
    // relying on per-Container pointerdown (unreliable on iOS/Android).
    this._btnBounds.push({ rect: hitArea, cb: onClick });

    return topY + btnH;
  }

  // ── Score card ────────────────────────────────────────────────────────────

  /**
   * Draws the themed score card.
   * Returns y of card's bottom edge.
   */
  private drawScoreCard(
    cx: number,
    topY: number,
    contentW: number,
    t0: number,
    t1: number,
  ): number {
    const cardH = 128;
    const cardW = contentW;
    const cy    = Math.round(topY + cardH / 2);

    // Card background
    const gfx = this.add.graphics();
    gfx.fillStyle(T.cardBg, 1);
    gfx.fillRoundedRect(
      Math.round(cx - cardW / 2), Math.round(topY),
      Math.round(cardW), cardH, T.cardR,
    );
    // Subtle gold border
    gfx.lineStyle(1, T.gold, 0.28);
    gfx.strokeRoundedRect(
      Math.round(cx - cardW / 2), Math.round(topY),
      Math.round(cardW), cardH, T.cardR,
    );

    // Layout: two rows inside the card
    const padX    = 20;
    const barX    = Math.round(cx - cardW / 2 + padX + 42); // after "US"/"THEM" label
    const barEndX = Math.round(cx + cardW / 2 - padX - 42); // before score number
    const barW    = barEndX - barX;
    const barH    = 7;
    const scoreX  = Math.round(cx + cardW / 2 - padX);

    const labelUs   = this.myTeam === 0 ? 'US'   : 'THEM';
    const labelThem = this.myTeam === 0 ? 'THEM' : 'US';
    const scoreUs   = this.myTeam === 0 ? t0 : t1;
    const scoreThem = this.myTeam === 0 ? t1 : t0;

    const rowAY = Math.round(cy - cardH * 0.16);
    const rowBY = Math.round(cy + cardH * 0.22);

    const isUsAhead   = scoreUs   >= scoreThem;
    const isThemAhead = scoreThem > scoreUs;

    // Row A — Us
    this.txt(Math.round(cx - cardW / 2 + padX), rowAY, labelUs, {
      fontSize:   '10px',
      color:      T.secondary,
      fontFamily: 'Montserrat, sans-serif',
      fontStyle:  'bold',
    }, 0, 0.5);

    this.drawBar(gfx, barX, rowAY, barW, barH, scoreUs, this.targetScore, T.teamBlue);

    this.txt(scoreX, rowAY, `${scoreUs}`, {
      fontSize:   '15px',
      color:      isUsAhead ? T.goldStr : T.secondary,
      fontFamily: 'Nunito, sans-serif',
      fontStyle:  'bold',
    }, 1, 0.5);

    // Row B — Them
    this.txt(Math.round(cx - cardW / 2 + padX), rowBY, labelThem, {
      fontSize:   '10px',
      color:      T.secondary,
      fontFamily: 'Montserrat, sans-serif',
      fontStyle:  'bold',
    }, 0, 0.5);

    this.drawBar(gfx, barX, rowBY, barW, barH, scoreThem, this.targetScore, T.teamRed);

    this.txt(scoreX, rowBY, `${scoreThem}`, {
      fontSize:   '15px',
      color:      isThemAhead ? T.goldStr : T.secondary,
      fontFamily: 'Nunito, sans-serif',
      fontStyle:  'bold',
    }, 1, 0.5);

    // "First to N" target marker
    const markerX = barX + barW;
    gfx.lineStyle(1, T.gold, 0.15);
    gfx.beginPath();
    gfx.moveTo(markerX, rowAY - barH * 2);
    gfx.lineTo(markerX, rowBY + barH * 2);
    gfx.strokePath();

    return topY + cardH;
  }

  private drawBar(
    gfx:    Phaser.GameObjects.Graphics,
    x:      number,
    cy:     number,
    barW:   number,
    barH:   number,
    score:  number,
    target: number,
    color:  number,
  ): void {
    const top  = Math.round(cy - barH / 2);
    const fill = Math.min(score / Math.max(target, 1), 1);

    gfx.fillStyle(T.barTrack, 1);
    gfx.fillRoundedRect(x, top, barW, barH, 3);

    if (fill > 0) {
      gfx.fillStyle(color, 0.9);
      gfx.fillRoundedRect(x, top, Math.round(barW * fill), barH, 3);
    }
  }

  // ── Round-over layout ─────────────────────────────────────────────────────

  private drawRoundOver(w: number, h: number, t0: number, t1: number): void {
    const gs         = this.gameState;
    const myTeamWon  = gs.winner_team === this.myTeam;
    const cx         = Math.round(w / 2);
    const contentW   = this.contentWidth(w);

    // How much vertical space do we have? Distribute via proportional anchors.
    let y = Math.round(h * 0.07);

    // ── Overline ─────────────────────────────────────────────────
    y = this.overline(cx, y, `ROUND ${gs.game_number ?? ''}`.trim());
    y += 14;

    // ── Result headline ───────────────────────────────────────────
    const headline = myTeamWon ? 'You Won\nthe Round' : 'You Lost\nthe Round';
    const headlineSz = IS_MOBILE_EXPERIENCE
      ? (w < 380 ? '28px' : '32px')
      : '36px';

    const headlineObj = this.txt(cx, y, headline, {
      fontSize:   headlineSz,
      color:      T.white,
      fontFamily: 'Nunito, sans-serif',
      fontStyle:  'bold',
      align:      'center',
    }, 0.5, 0);
    y += headlineObj.height + 16;

    // ── Win type ──────────────────────────────────────────────────
    const winType = gs.game_end_type === 'blocked' ? 'Blocked game' : 'By domino';
    this.txt(cx, y, winType, {
      fontSize:   '13px',
      color:      T.muted,
      fontFamily: 'Montserrat, sans-serif',
    }, 0.5, 0);
    y += 28;

    this.divider(cx, y, contentW);
    y += 20;

    // ── Session score section ─────────────────────────────────────
    this.txt(cx, y, 'SESSION SCORE', {
      fontSize:      '10px',
      color:         T.goldStr,
      fontFamily:    'Montserrat, sans-serif',
      fontStyle:     'bold',
      letterSpacing: 2,
    }, 0.5, 0);
    y += 18;

    y = this.drawScoreCard(cx, y, contentW, t0, t1);
    y += 10;

    this.txt(cx, y, `First to ${this.targetScore} wins the session`, {
      fontSize:   '11px',
      color:      T.muted,
      fontFamily: 'Montserrat, sans-serif',
    }, 0.5, 0);
    y += 28;

    this.divider(cx, y, contentW);
    y += 28;

    // ── Next Round button ─────────────────────────────────────────
    const btnBottom = this.goldButton(cx, y, contentW, 'Next Round', () => this.handleNextGame());
    y = btnBottom + 18;

    // ── Countdown hint ────────────────────────────────────────────
    this.countdownText = this.txt(cx, y, 'Starting in 5…', {
      fontSize:   '12px',
      color:      T.muted,
      fontFamily: 'Montserrat, sans-serif',
    }, 0.5, 0);

    this.startAutoAdvance();
    this.startFallbackWatchdog();
    this._registerBtnRouter();
  }

  // ── Session-over layout ───────────────────────────────────────────────────

  private drawSessionOver(
    w:          number,
    h:          number,
    t0:         number,
    t1:         number,
    winnerTeam: number,
  ): void {
    const myTeamWon = winnerTeam === this.myTeam;
    const cx        = Math.round(w / 2);
    const contentW  = this.contentWidth(w);

    // ── Gold top accent line ──────────────────────────────────────
    const accentGfx = this.add.graphics();
    const accentH   = 3;
    const accentW   = Math.round(contentW * 0.55);
    accentGfx.fillStyle(T.gold, myTeamWon ? 1 : 0.35);
    accentGfx.fillRoundedRect(Math.round(cx - accentW / 2), 0, accentW, accentH, 1);

    let y = Math.round(h * 0.09);

    // ── Overline ──────────────────────────────────────────────────
    y = this.overline(cx, y, 'SESSION OVER');
    y += 18;

    // ── Victory / Defeat ──────────────────────────────────────────
    const resultColor = myTeamWon ? T.goldStr : '#ffffff';
    const resultSz    = IS_MOBILE_EXPERIENCE
      ? (w < 380 ? '38px' : '44px')
      : '52px';

    const resultObj = this.txt(cx, y, myTeamWon ? 'Victory' : 'Defeat', {
      fontSize:   resultSz,
      color:      resultColor,
      fontFamily: 'Nunito, sans-serif',
      fontStyle:  'bold',
    }, 0.5, 0);
    y += resultObj.height + 14;

    // ── Subtitle ──────────────────────────────────────────────────
    const winnerLabel = winnerTeam === 0 ? 'Team A' : 'Team B';
    this.txt(cx, y, `${winnerLabel} wins the match`, {
      fontSize:   '14px',
      color:      T.secondary,
      fontFamily: 'Montserrat, sans-serif',
    }, 0.5, 0);
    y += 30;

    this.divider(cx, y, contentW);
    y += 20;

    // ── Final score section ───────────────────────────────────────
    this.txt(cx, y, 'FINAL SCORE', {
      fontSize:      '10px',
      color:         T.goldStr,
      fontFamily:    'Montserrat, sans-serif',
      fontStyle:     'bold',
      letterSpacing: 2,
    }, 0.5, 0);
    y += 18;

    y = this.drawScoreCard(cx, y, contentW, t0, t1);
    y += 28;

    this.divider(cx, y, contentW);
    y += 28;

    // ── Back Home button ──────────────────────────────────────────
    const btnBottom = this.goldButton(cx, y, contentW, 'Back Home', () => {
      recordGameExit('session_finished', { note: 'game-over-back-home' });
      this.sessionChannel?.unsubscribe();
      this.scene.start('MenuScene');
    });
    y = btnBottom + 22;

    // ── Thanks line ───────────────────────────────────────────────
    this.txt(cx, y, 'Thanks for playing', {
      fontSize:   '12px',
      color:      T.muted,
      fontFamily: 'Montserrat, sans-serif',
    }, 0.5, 0);

    this._registerBtnRouter();
  }

  // ── Auto-advance countdown ────────────────────────────────────────────────

  private startAutoAdvance(): void {
    let remaining = 5;

    this.autoAdvanceTimer = this.time.addEvent({
      delay:    1000,
      repeat:   4,
      callback: () => {
        remaining -= 1;
        if (!this.countdownText) return;
        if (remaining > 0) {
          this.countdownText.setText(`Starting in ${remaining}…`);
        } else {
          this.countdownText.setText('');
          this.handleNextGame();
        }
      },
    });
  }

  private cancelAutoAdvance(): void {
    this.autoAdvanceTimer?.remove(false);
    this.autoAdvanceTimer = null;
    this.countdownText?.setText('');
  }

  private startFallbackWatchdog(): void {
    // If we are still in the scene 9 seconds after round-over and haven't
    // transitioned yet, poll the session to find any already-created next game.
    this.fallbackTimer = this.time.delayedCall(9000, () => {
      console.log(
        `[DIAG:GameOver] watchdog fired session=${this.sessionId}` +
        ` state=${this.transitionState} targetId=${this.transitionTargetGameId ?? 'none'}` +
        ` active=${this.scene.isActive()}`,
      );

      if (!this.scene.isActive()) return;

      // Detect invalid stale state: transitioning but no target game id means
      // scene.start was never actually called. Reset and recover.
      if (this.transitionState === 'transitioning' && this.transitionTargetGameId == null) {
        console.warn(
          `[DIAG:GameOver] watchdog: INVALID STATE — transitioning with no targetId,` +
          ` resetting to idle and recovering session=${this.sessionId}`,
        );
        this.setTransitionState('idle', 'watchdog-stale-reset');
        this.transitionTargetGameId = undefined;
        void this.tryFallbackTransition();
        return;
      }

      // Already in a valid transitioning state — scene.start was called; nothing to do.
      if (this.transitionState === 'transitioning') return;

      // creating_next: REST is still in flight after 9s — something stalled.
      // Reset and recover so the user isn't stuck.
      if (this.transitionState === 'creating_next') {
        console.warn(
          `[DIAG:GameOver] watchdog: REST still in flight after 9s — resetting session=${this.sessionId}`,
        );
        this.setTransitionState('idle', 'watchdog-creating-timeout');
        void this.tryFallbackTransition();
        return;
      }

      // idle — normal watchdog path: poll session for the next game
      void this.tryFallbackTransition();
    });
  }

  private cancelFallbackWatchdog(): void {
    this.fallbackTimer?.remove(false);
    this.fallbackTimer = null;
  }

  private setCountdownText(text: string): void {
    this.countdownText?.setText(text);
  }

  /** Central state mutator — logs every transition for post-mortem tracing. */
  private setTransitionState(
    next:   'idle' | 'creating_next' | 'transitioning',
    source: string,
  ): void {
    console.log(
      `[DIAG:GameOver] state ${this.transitionState} → ${next}` +
      ` source=${source} session=${this.sessionId}` +
      ` targetId=${this.transitionTargetGameId ?? 'none'}` +
      ` active=${this.scene?.isActive() ?? '?'}`,
    );
    this.transitionState = next;
  }

  // TypeScript narrows `transitionState` after assignments and early-return guards,
  // so we can't compare it to 'transitioning' inside the same scope after those.
  // This method defeats that narrowing: TypeScript cannot analyse through a method call.
  private isTransitioning(): boolean {
    return this.transitionState === 'transitioning';
  }

  // ── Single authoritative transition point ─────────────────────────────────

  /**
   * All navigation paths (WS, REST, fallback, button) funnel through here.
   * Exactly one call wins; all subsequent calls for the same target are
   * no-ops and conflicts (different gameId) are logged and ignored.
   */
  private transitionToNextGame(
    gameId:      number,
    source:      string,
    teamScores?: Record<string, number>,
  ): void {
    console.log(
      `[DIAG:GameOver] transitionToNextGame source=${source} gameId=${gameId}` +
      ` state=${this.transitionState} targetId=${this.transitionTargetGameId ?? 'none'}` +
      ` active=${this.scene.isActive()}`,
    );

    if (!this.scene.isActive()) {
      console.log(`[DIAG:GameOver] transitionToNextGame: scene inactive — ignored (${source})`);
      return;
    }

    if (this.transitionState === 'transitioning') {
      if (this.transitionTargetGameId === gameId) {
        console.log(`[DIAG:GameOver] transitionToNextGame: already transitioning to same gameId=${gameId} — no-op (${source})`);
      } else {
        console.warn(
          `[DIAG:GameOver] transitionToNextGame: CONFLICT — already transitioning to` +
          ` gameId=${this.transitionTargetGameId}, ignoring gameId=${gameId} (${source})`,
        );
      }
      return;
    }

    this.setTransitionState('transitioning', source);
    this.transitionTargetGameId = gameId;

    this.cancelAutoAdvance();
    this.cancelFallbackWatchdog();
    this.sessionChannel?.unsubscribe();
    this.sessionChannel = null;

    console.log(`[DIAG:GameOver] transitionToNextGame: scene.start GameScene gameId=${gameId} (${source})`);
    this.scene.start('GameScene', {
      gameId,
      sessionId:        this.sessionId,
      teamScores:       teamScores ?? this.teamScores,
      useDesktopRender: this.useDesktopRender,
    });
  }

  // ── Next game logic ───────────────────────────────────────────────────────

  private async handleNextGame(): Promise<void> {
    console.log(
      `[DIAG:GameOver] handleNextGame START session=${this.sessionId}` +
      ` state=${this.transitionState} active=${this.scene.isActive()}` +
      ` timerAlive=${this.autoAdvanceTimer !== null}`,
    );

    if (this.transitionState !== 'idle') {
      console.log(`[DIAG:GameOver] handleNextGame: state=${this.transitionState} — early return`);
      return;
    }

    this.setTransitionState('creating_next', 'handleNextGame');
    this.cancelAutoAdvance();

    try {
      console.log(`[DIAG:GameOver] handleNextGame: REST nextGame session=${this.sessionId}`);
      const data = await apiClient.nextGame(this.sessionId);
      console.log(
        `[DIAG:GameOver] handleNextGame: REST resolved game=${data.game_id}` +
        ` state=${this.transitionState} active=${this.scene.isActive()}`,
      );

      if (!this.scene.isActive()) {
        console.log(`[DIAG:GameOver] handleNextGame: scene inactive after REST — ignored`);
        return;
      }

      if (this.isTransitioning()) {
        // WS arrived first and already called scene.start — REST is a no-op.
        console.log(`[DIAG:GameOver] handleNextGame: WS already won — REST no-op game=${data.game_id}`);
        return;
      }

      this.transitionToNextGame(data.game_id, 'REST', data.team_scores);
    } catch (err) {
      console.error(`[DIAG:GameOver] handleNextGame: REST FAILED session=${this.sessionId}`, err);

      if (!this.scene.isActive()) return;
      if (this.isTransitioning()) return;

      // Reset to idle so the user can retry via the button.
      this.setTransitionState('idle', 'handleNextGame-error');
      this.setCountdownText('Retrying…');
      void this.tryFallbackTransition();
    }
  }

  // ── Fallback / recovery ───────────────────────────────────────────────────

  private async tryFallbackTransition(): Promise<void> {
    console.log(
      `[DIAG:GameOver] tryFallbackTransition session=${this.sessionId}` +
      ` state=${this.transitionState} active=${this.scene.isActive()}`,
    );

    if (this.isTransitioning()) return;
    if (!this.scene.isActive()) return;

    try {
      const session = await apiClient.getSession(this.sessionId);
      console.log(
        `[DIAG:GameOver] tryFallbackTransition: current_game=${session.current_game}` +
        ` state=${this.transitionState} active=${this.scene.isActive()}`,
      );

      if (!this.scene.isActive()) return;
      if (this.isTransitioning()) return;

      if (session.current_game) {
        this.transitionToNextGame(session.current_game, 'fallback', session.team_scores);
      } else {
        console.warn(
          `[DIAG:GameOver] tryFallbackTransition: no current_game yet — showing retry session=${this.sessionId}`,
        );
        this.setCountdownText('Tap "Next Round" to continue');
      }
    } catch (fetchErr) {
      console.error(`[DIAG:GameOver] tryFallbackTransition: getSession FAILED`, fetchErr);
      if (this.scene.isActive() && !this.isTransitioning()) {
        this.setCountdownText('Tap "Next Round" to continue');
      }
    }
  }

  // ── Scene-level button tap router ─────────────────────────────────────────

  private _registerBtnRouter(): void {
    this._teardownBtnRouter();
    this._btnRouter = (ptr: Phaser.Input.Pointer) => {
      for (const btn of this._btnBounds) {
        if (btn.rect.contains(ptr.x, ptr.y)) {
          btn.cb();
          return;
        }
      }
    };
    this.input.on('pointerdown', this._btnRouter);
  }

  private _teardownBtnRouter(): void {
    if (this._btnRouter) {
      this.input.off('pointerdown', this._btnRouter);
      this._btnRouter = null;
    }
    this._btnBounds = [];
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  shutdown(): void {
    console.log(
      `[DIAG:GameOver] shutdown session=${this.sessionId}` +
      ` state=${this.transitionState} targetId=${this.transitionTargetGameId ?? 'none'}` +
      ` timerAlive=${this.autoAdvanceTimer !== null} channelAlive=${this.sessionChannel !== null}`,
    );
    // Do NOT reset transitionState — stale async continuations check
    // scene.isActive() and transitionState before touching anything.
    this.cancelAutoAdvance();
    this.cancelFallbackWatchdog();
    this._teardownBtnRouter();
    this.sessionChannel?.unsubscribe();
    this.sessionChannel = null;
  }
}
